<template>
    <table :ref="setTableElement" class="w-full">
        <thead>
            <!-- When a column is focusable, its min-h-10 provides bottom spacing; remove mb-2 to avoid double gap -->
            <tr class="flex gap-x-3 rounded text-[0.9rem] text-gray-500" :class="[narrow ? 'px-4' : 'px-6', hasFocusedColumn ? 'mt-2' : 'my-2']">
                <slot
                    v-for="header of headers"
                    :key="header.value"
                    :name="[`header_${header.value}`]"
                    :header="header"
                    :set-item-ref="setItemRef"
                    :get-header-class="getHeaderClass"
                    :on-header-clicked="onHeaderClicked"
                    :get-header-sort-icon="getHeaderSortIcon"
                >
                    <th :key="header.value" :ref="(el) => setItemRef(el as Element | null, header)" class="flex flex-1" :class="[
                            `${getHeaderClass(header, header.focusable || (header.sortable && canSort))}`,
                            { 'rounded-lg text-white transition-colors min-h-10 px-3 py-2': header.focusable },
                            { 'bg-primary-700 hover:bg-primary-800': header.focusable && header.value !== focusedColumn },
                            { 'bg-primary-800': header.value === focusedColumn }
                        ]" @click="onHeaderClicked(header)">
                        <div class="flex font-medium">
                            <span v-html="sanitizeHtml(header.text)"></span>
                            <span v-if="header.sortable && canSort" class="sort-icon flex ml-1.5" :class="{ visible: getHeaderSortIcon(header).active }">
                                <Icon class="my-auto min-w-2 w-2 h-3.5" :src="getHeaderSortIcon(header).icon"></Icon>
                            </span>
                        </div>
                    </th>
                </slot>
            </tr>
        </thead>
        <tbody v-if="sortedItems?.length">
            <slot name="prepend-rows" :get-width="getWidth" :get-item-property="getItemProperty" :get-header-class="getHeaderClass" :clickable-items="clickableItems" :focused-column="focusedColumn" />
            <template v-for="(item, index) of sortedItems" :key="getRowKey(item, index)">
                <tr
                    :class="[
                        { 'cursor-pointer': clickableItems, 'odd:bg-dark-gray-200 rounded': !expandItemsKey && !rowClass?.(item), 'border-t border-dark-gray-300': expandItemsKey },
                        narrow ? 'px-4' : 'px-6',
                        rowClass?.(item),
                    ]"
                    class="flex py-4 gap-x-3 text-[0.9rem]"
                    @click="clickableItems && $emit('item-clicked', item)"
                >
                    <slot
                        v-for="header of headers"
                        :key="`${getRowKey(item, index)}_${header.value}`"
                        :name="[`${header.value}`]"
                        :item="item"
                        :get-width="getWidth"
                        :get-header-class="getHeaderClass"
                        :header="header"
                        :index="index"
                        :get-item-property="getItemProperty"
                        :expanded="isExpanded(index)"
                    >
                        <!-- -my-4 cancels the row's py-4 padding so the bg fills the full row height -->
                        <td
                            :key="`${getRowKey(item, index)}_${header.value}`"
                            :class="[`${getHeaderClass(header, clickableItems)} ${header.cellClass ? header.cellClass : ''} break-words min-h-6.5`, { 'my-auto': header.value !== focusedColumn, 'self-stretch -my-4 flex items-center bg-primary-700/20': header.value === focusedColumn }]"
                            :style="`max-width: ${getWidth(header)}px; width: 100%;`"
                        >
                            {{ getItemProperty(header, item, { numberFormat: header.numberFormat }) }}
                        </td>
                    </slot>
                </tr>
                <ExpandCollapseTransition>
                    <div v-if="isExpanded(index) && item[expandItemsKey]?.length" class="expand-container bg-dark-gray-200" :class="{ 'expand-container--last': index === sortedItems.length - 1 }">
                        <tr
                            v-for="(expandItem, expandItemIndex) of item[expandItemsKey]"
                            :key="`expand_${getRowKey(item, index)}_${expandItemIndex}`"
                            class="flex py-4 gap-x-3 bg-dark-gray-200 text-[0.9rem]"
                            :class="[{ 'border-t border-dark-gray-400': expandItemIndex !== 0 }, narrow ? 'px-4' : 'px-6']"
                        >
                            <slot
                                v-for="header of headers"
                                :key="`${getRowKey(item, index)}_${header.value}`"
                                :name="[`${header.value}-expand`]"
                                :item="expandItem"
                                :get-width="getWidth"
                                :get-header-class="getHeaderClass"
                                :header="header"
                                :index="index"
                                :get-item-property="getItemProperty"
                            >
                                <!-- -my-4 cancels the row's py-4 padding so the bg fills the full row height -->
                                <td
                                    :key="`${getRowKey(item, index)}_${expandItemIndex}_${header.value}`"
                                    :class="[`${getHeaderClass(header, clickableItems)} break-words min-h-6.5`, { 'my-auto': header.value !== focusedColumn, 'self-stretch -my-4 flex items-center bg-primary-700/20': header.value === focusedColumn }]"
                                    :style="`max-width: ${getWidth(header)}px; width: 100%;`"
                                >
                                    {{ getItemProperty(header, expandItem, { numberFormat: header.numberFormat }) }}
                                </td>
                            </slot>
                        </tr>
                    </div>
                </ExpandCollapseTransition>
                <ExpandCollapseTransition>
                    <div v-if="isExpanded(index) && !expandItemsKey" class="flex py-4 border-t border-b border-t-gray-200 border-b-gray-200" :class="narrow ? 'px-4' : 'px-6'">
                        <slot name="expanded-row" :item="item"></slot>
                    </div>
                </ExpandCollapseTransition>
            </template>
            <slot name="append-rows" :get-width="getWidth" :get-item-property="getItemProperty" :get-header-class="getHeaderClass" :focused-column="focusedColumn"></slot>
        </tbody>
        <tbody v-else>
            <tr class="rounded flex py-4 gap-x-3 odd:bg-dark-gray-200" :class="narrow ? 'px-4' : 'px-6'">
                <td class="text-center w-full font-medium">
                    {{ $t('views.activityTable.table.emptyTableMessage') }}
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch, onMounted } from 'vue';
import sanitizeHtml from 'sanitize-html';
import { TableHeader } from '@/types';
import useGetItemProperty from '@/components/common/composables/useGetItemProperty';
import ExpandCollapseTransition from '@/components/common/ExpandCollapseTransition.vue';
import useResizableTable from '@/composables/useResizableTable';
import useSortableTable from '@/composables/useSortableTable';

export default defineComponent({
    components: { ExpandCollapseTransition },
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        rowKey: {
            type: String,
            default: '',
        },
        headers: {
            type: Array as () => TableHeader[],
            default: () => [],
        },
        clickableItems: {
            type: Boolean,
            default: false,
        },
        expandedItems: {
            type: Array,
            default: () => [],
        },
        expandItemsKey: {
            type: String,
            default: '',
        },
        resizeDelay: {
            type: Number,
            default: 0,
        },
        rounded: {
            type: Boolean,
            default: true,
        },
        narrow: {
            type: Boolean,
            default: false,
        },
        rowClass: {
            type: Function as unknown as () => (item: unknown) => string,
            default: null,
        },
        canSort: {
            type: Boolean,
            default: true,
        },
        focusedColumn: {
            type: String as () => string | null,
            default: null,
        },
    },
    emits: ['item-clicked', 'sort', 'focus-column'],
    setup(props, { emit }) {
        const { getWidth, scheduleResize, setItemRef, setTableRef } = useResizableTable({ resizeDelay: props.resizeDelay });
        // A native <table>, so the ref is always an Element, never a component instance.
        const setTableElement = (el: unknown) => setTableRef(el as Element | null);
        const items = computed(() => props.items);
        const { sortedItems, getHeaderSortIcon, onHeaderClicked: headerClicked } = useSortableTable(items, props.headers);
        const focusedColumn = ref<string | null>(props.focusedColumn);

        watch(() => props.focusedColumn, (val) => {
            focusedColumn.value = val ?? null;
        });

        watch(
            () => ({
                headers: props.headers,
                itemsLength: props.items.length,
                narrow: props.narrow,
                focusedColumn: props.focusedColumn,
            }),
            () => {
                scheduleResize();
            },
            { deep: true, flush: 'post' }
        );

        const hasFocusedColumn = computed(() => props.headers.some((h) => h.focusable));

        function getHeaderClass(header: TableHeader, clickable?: boolean) {
            let result = '';
            switch (header.align) {
                case 'left': {
                    result = 'text-left';
                    break;
                }
                case 'right': {
                    result = 'text-right justify-end';
                    break;
                }
                default: {
                    result = 'text-center justify-center';
                }
            }

            result += clickable ? ' cursor-pointer' : ' cursor-auto';
            result += header.headerClass ? ` ${header.headerClass}` : '';

            return result;
        }

        function isExpanded(rowIndex: number) {
            return props.expandedItems.includes(rowIndex);
        }

        function onHeaderClicked(header: TableHeader) {
            if (header.focusable) {
                if (focusedColumn.value !== header.value) {
                    focusedColumn.value = header.value;
                    emit('focus-column', focusedColumn.value);
                }
                return;
            }

            if (props.canSort) {
                headerClicked(header);

                if (header.sortable) {
                    emit('sort');
                }
            }
        }

        if (process.env.NODE_ENV !== 'production') {
            onMounted(() => {
                props.headers.forEach((h) => {
                    if (h.focusable && h.sortable) {
                        console.warn(`[CustomTable] Header "${h.value}" has both focusable and sortable set. Only one can apply — focusable takes precedence.`);
                    }
                });
            });
        }

        return {
            getHeaderClass,
            getRowKey: (item: Record<string, unknown>, index: number) => (props.rowKey && item?.[props.rowKey] !== undefined ? item[props.rowKey] : index),
            sanitizeHtml,
            getItemProperty: useGetItemProperty,
            isExpanded,
            getWidth,
            setTableElement,
            setItemRef,
            sortedItems,
            getHeaderSortIcon,
            onHeaderClicked,
            hasFocusedColumn,
            focusedColumn,
        };
    },
});
</script>

<style scoped>
th .sort-icon {
    visibility: hidden;
}
th:hover .sort-icon {
    visibility: visible;
}

.expand-container {
    position: relative;
}

.expand-container:after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    box-shadow: inset 0px 8px 8px -8px rgba(0, 0, 0, 0.25), inset 0px -8px 8px -8px rgba(0, 0, 0, 0.2);
    pointer-events: none;
}

.expand-container--last:after {
    box-shadow: inset 0px 8px 8px -8px rgba(0, 0, 0, 0.25);
}
</style>
